import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  adminLogin,
  adminMe,
  adminListInquiries,
  adminDeleteInquiry,
  adminListArticles,
  adminCreateArticle,
  adminUpdateArticle,
  adminDeleteArticle,
  adminListPodcasts,
  adminCreatePodcast,
  adminUpdatePodcast,
  adminDeletePodcast,
  fetchSiteImages,
  adminUploadImage,
  adminUpdateSiteImage,
  adminResetSiteImage,
  extractErrorMessage,
} from "@/lib/api";
import { refreshSiteImages } from "@/lib/useSiteImages";
import { getToken, setToken, clearToken, hasToken } from "@/lib/auth";
import { ADMIN } from "@/constants/testIds";

const TABS = [
  { key: "inquiries", label: "Inquiries" },
  { key: "articles", label: "Articles" },
  { key: "podcasts", label: "Podcasts" },
  { key: "images", label: "Hero Images" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
function absolutize(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

const IMAGE_SLOTS = [
  {
    key: "heroPortrait",
    label: "Home — Hero portrait",
    hint: "Right side of the Home dark hero. Editorial, ~4:5 crop. JPG/PNG/WebP, max 8 MB.",
  },
  {
    key: "aboutPortrait",
    label: "About — Page portrait",
    hint: "Right side of the About hero. Slightly more contemplative is fine.",
  },
  {
    key: "speakingPortrait",
    label: "Speaking — Page portrait",
    hint: "Right side of the Speaking hero. Stage shots welcome.",
  },
  {
    key: "advisoryPortrait",
    label: "Advisory — Page portrait",
    hint: "Right side of the Advisory hero. Confident, executive frame.",
  },
];

function ImageSlot({ slot, currentUrl, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputId = `img-${slot.key}`;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await adminUploadImage(file);
      const updated = await adminUpdateSiteImage(slot.key, url);
      await refreshSiteImages();
      onChange(updated);
      toast.success(`${slot.label.split(" — ")[0]} image updated.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
      // Reset the input so the same file can be re-uploaded if needed.
      e.target.value = "";
    }
  };

  const onReset = async () => {
    if (!window.confirm("Reset this image to the default?")) return;
    setBusy(true);
    try {
      const updated = await adminResetSiteImage(slot.key);
      await refreshSiteImages();
      onChange(updated);
      toast.success(`${slot.label.split(" — ")[0]} image reset.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const preview = absolutize(currentUrl);
  const isCustom =
    currentUrl &&
    !currentUrl.startsWith("/images/rakia-");

  return (
    <article
      className="rk-card"
      style={{ backgroundColor: "var(--rk-bg)", padding: 0, overflow: "hidden" }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4/5",
          backgroundColor: "var(--rk-surface)",
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt={slot.label}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--rk-muted)",
              fontSize: 13,
            }}
          >
            No image
          </div>
        )}
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            padding: "4px 10px",
            fontSize: 11,
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            backgroundColor: isCustom ? "var(--rk-copper)" : "rgba(22,24,27,0.7)",
            color: "var(--rk-cream)",
            borderRadius: 2,
          }}
        >
          {isCustom ? "Custom" : "Default"}
        </div>
      </div>
      <div style={{ padding: 22 }}>
        <div
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: 22,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          {slot.label}
        </div>
        <p className="mt-2 rk-caption" style={{ color: "var(--rk-muted)" }}>
          {slot.hint}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onPick}
            data-testid={`admin-image-upload-input-${slot.key}`}
            style={{ display: "none" }}
            disabled={busy}
          />
          <label
            htmlFor={inputId}
            className="rk-btn-primary"
            style={{
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.6 : 1,
              height: 40,
              padding: "0 18px",
              fontSize: 14,
            }}
          >
            {busy ? "Uploading…" : isCustom ? "Replace" : "Upload"}
          </label>
          {isCustom && (
            <button
              type="button"
              onClick={onReset}
              disabled={busy}
              data-testid={`admin-image-reset-button-${slot.key}`}
              className="rk-btn-outline"
              style={{ height: 40, padding: "0 18px", fontSize: 14 }}
            >
              Reset to default
            </button>
          )}
        </div>
        {currentUrl && (
          <p
            className="mt-4 rk-caption"
            style={{ color: "var(--rk-muted)", wordBreak: "break-all" }}
          >
            {currentUrl}
          </p>
        )}
      </div>
    </article>
  );
}

function ImagesView() {
  const [images, setImages] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchSiteImages()
      .then((d) => active && setImages(d))
      .catch((e) => active && toast.error(extractErrorMessage(e)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return <p className="rk-caption">Loading…</p>;
  }
  if (!images) {
    return <p className="rk-caption">Failed to load images.</p>;
  }

  return (
    <div>
      <p
        className="rk-body mb-8"
        style={{ color: "var(--rk-muted)", maxWidth: 720 }}
      >
        Upload replacement photography for each page hero. Changes are live
        immediately. Use the Reset action to fall back to the original photo.
      </p>
      <div
        data-testid="admin-images-grid"
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {IMAGE_SLOTS.map((slot) => (
          <ImageSlot
            key={slot.key}
            slot={slot}
            currentUrl={images[slot.key]}
            onChange={setImages}
          />
        ))}
      </div>
    </div>
  );
}

function LoginPanel({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { token } = await adminLogin(password);
      setToken(token);
      onLoggedIn();
      toast.success("Signed in.");
    } catch (e2) {
      setErr(extractErrorMessage(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rk-section" style={{ paddingTop: 160 }}>
      <div className="rk-container-text">
        <div className="rk-eyebrow">Admin</div>
        <h1 className="rk-heading-page mt-5">Sign in.</h1>
        <p className="mt-6 rk-body" style={{ color: "var(--rk-muted)" }}>
          Single password access for inquiries, articles, and podcast
          appearances.
        </p>
        <form
          onSubmit={onSubmit}
          className="mt-10 max-w-[440px] flex flex-col gap-5"
        >
          <div>
            <label htmlFor="adm-pw" className="rk-label">Password</label>
            <input
              id="adm-pw"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={ADMIN.loginPasswordInput}
              className="rk-field"
              placeholder="••••••••••••••••"
            />
          </div>
          {err && (
            <div
              data-testid={ADMIN.loginError}
              style={{ color: "var(--rk-danger)", fontSize: 14 }}
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid={ADMIN.loginSubmit}
            className="rk-btn-primary"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </section>
  );
}

function InquiriesView() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (f) => {
    setLoading(true);
    try {
      const data = await adminListInquiries(f || undefined);
      setItems(data.items || []);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(filter);
  }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await adminDeleteInquiry(id);
      await load(filter);
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="rk-eyebrow">{items.length} record{items.length === 1 ? "" : "s"}</div>
        <select
          data-testid={ADMIN.inquiriesFilter}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rk-field rk-select"
          style={{ width: 240, height: 40 }}
        >
          <option value="">All</option>
          <option value="contact">Contact</option>
          <option value="speaking">Speaking</option>
          <option value="advisory">Advisory</option>
          <option value="newsletter">Newsletter</option>
        </select>
      </div>
      <div
        data-testid={ADMIN.inquiriesTable}
        style={{ border: "1px solid var(--rk-border)" }}
      >
        <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: "var(--rk-surface)" }}>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Date</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Type</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>From</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Summary</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Webhook</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-6 px-4 rk-caption">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 px-4 rk-caption">No records.</td></tr>
            ) : (
              items.map((it, i) => {
                const p = it.payload || {};
                const summary =
                  p.message ||
                  p.situation ||
                  p.event_name_date ||
                  (p.email && `Subscribed: ${p.email}`) ||
                  "—";
                const from = p.name
                  ? `${p.name}${p.email ? " · " + p.email : ""}`
                  : p.email || "—";
                return (
                  <tr key={it.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--rk-border)" }}>
                    <td className="py-3 px-4 align-top rk-caption">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-3 px-4 align-top" style={{ textTransform: "capitalize" }}>{it.form_type}</td>
                    <td className="py-3 px-4 align-top">{from}</td>
                    <td className="py-3 px-4 align-top" style={{ maxWidth: 360 }}>{summary}</td>
                    <td className="py-3 px-4 align-top rk-caption">
                      {it.webhook?.sent ? `✓ ${it.webhook.status_code || ""}` : "—"}
                    </td>
                    <td className="py-3 px-4 align-top">
                      <button onClick={() => remove(it.id)} className="rk-btn-ghost" style={{ color: "var(--rk-danger)" }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArticleEditor({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(
    initial || { title: "", summary: "", url: "", published_date: "", is_published: true, body: "" }
  );
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (initial?.id) await adminUpdateArticle(initial.id, body);
      else await adminCreateArticle(body);
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rk-card mb-6" style={{ backgroundColor: "var(--rk-surface)" }}>
      <h3 style={{ fontFamily: "var(--font-heading)", fontSize: 24 }}>
        {initial?.id ? "Edit article" : "New article"}
      </h3>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="rk-label">Title</label>
          <input className="rk-field" value={form.title} onChange={update("title")} />
        </div>
        <div className="md:col-span-2">
          <label className="rk-label">Summary</label>
          <textarea className="rk-textarea" value={form.summary || ""} onChange={update("summary")} />
        </div>
        <div>
          <label className="rk-label">External URL</label>
          <input className="rk-field" value={form.url || ""} onChange={update("url")} placeholder="https://..." />
        </div>
        <div>
          <label className="rk-label">Published date</label>
          <input type="date" className="rk-field" value={(form.published_date || "").slice(0, 10)} onChange={update("published_date")} />
        </div>
        <div className="md:col-span-2">
          <label className="rk-label">Body (optional)</label>
          <textarea className="rk-textarea" value={form.body || ""} onChange={update("body")} style={{ minHeight: 200 }} />
        </div>
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={!!form.is_published} onChange={update("is_published")} /> Published
          </label>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={save} disabled={saving} className="rk-btn-primary">
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="rk-btn-outline">Cancel</button>
      </div>
    </div>
  );
}

function ArticlesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | {id...}

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListArticles());
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await adminDeleteArticle(id);
      await load();
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="rk-eyebrow">{items.length} article{items.length === 1 ? "" : "s"}</div>
        {!editing && (
          <button onClick={() => setEditing("new")} data-testid={ADMIN.newArticleBtn} className="rk-btn-primary" style={{ height: 40 }}>New article</button>
        )}
      </div>
      {editing && (
        <ArticleEditor
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); toast.success("Saved."); }}
        />
      )}
      <div data-testid={ADMIN.articlesTable} style={{ border: "1px solid var(--rk-border)" }}>
        <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: "var(--rk-surface)" }}>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Title</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Date</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Status</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="py-6 px-4 rk-caption">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="py-6 px-4 rk-caption">No articles.</td></tr>
            ) : items.map((a, i) => (
              <tr key={a.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--rk-border)" }}>
                <td className="py-3 px-4 align-top">{a.title}</td>
                <td className="py-3 px-4 align-top rk-caption">{(a.published_date || a.created_at || "").slice(0,10)}</td>
                <td className="py-3 px-4 align-top">{a.is_published ? "Published" : "Draft"}</td>
                <td className="py-3 px-4 align-top">
                  <div className="flex gap-3">
                    <button onClick={() => setEditing(a)} className="rk-btn-ghost">Edit</button>
                    <button onClick={() => remove(a.id)} className="rk-btn-ghost" style={{ color: "var(--rk-danger)" }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PodcastEditor({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(
    initial || { show_name: "", episode_title: "", date: "", url: "", description: "", is_published: true }
  );
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (initial?.id) await adminUpdatePodcast(initial.id, body);
      else await adminCreatePodcast(body);
      onSaved();
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rk-card mb-6" style={{ backgroundColor: "var(--rk-surface)" }}>
      <h3 style={{ fontFamily: "var(--font-heading)", fontSize: 24 }}>
        {initial?.id ? "Edit podcast" : "New podcast appearance"}
      </h3>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="rk-label">Show name</label>
          <input className="rk-field" value={form.show_name} onChange={update("show_name")} />
        </div>
        <div>
          <label className="rk-label">Episode title</label>
          <input className="rk-field" value={form.episode_title} onChange={update("episode_title")} />
        </div>
        <div>
          <label className="rk-label">Date</label>
          <input type="date" className="rk-field" value={(form.date || "").slice(0,10)} onChange={update("date")} />
        </div>
        <div>
          <label className="rk-label">URL</label>
          <input className="rk-field" value={form.url || ""} onChange={update("url")} placeholder="https://..." />
        </div>
        <div className="md:col-span-2">
          <label className="rk-label">Description</label>
          <textarea className="rk-textarea" value={form.description || ""} onChange={update("description")} />
        </div>
        <div>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={!!form.is_published} onChange={update("is_published")} /> Published
          </label>
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={save} disabled={saving} className="rk-btn-primary">{saving ? "Saving…" : "Save"}</button>
        <button onClick={onCancel} className="rk-btn-outline">Cancel</button>
      </div>
    </div>
  );
}

function PodcastsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListPodcasts());
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await adminDeletePodcast(id);
      await load();
      toast.success("Deleted.");
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="rk-eyebrow">{items.length} entr{items.length === 1 ? "y" : "ies"}</div>
        {!editing && (
          <button onClick={() => setEditing("new")} data-testid={ADMIN.newPodcastBtn} className="rk-btn-primary" style={{ height: 40 }}>New entry</button>
        )}
      </div>
      {editing && (
        <PodcastEditor
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); toast.success("Saved."); }}
        />
      )}
      <div data-testid={ADMIN.podcastsTable} style={{ border: "1px solid var(--rk-border)" }}>
        <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: "var(--rk-surface)" }}>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Show</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Episode</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Date</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}>Status</th>
              <th className="text-left py-3 px-4 rk-eyebrow" style={{ borderBottom: "1px solid var(--rk-copper)" }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-6 px-4 rk-caption">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 px-4 rk-caption">No entries.</td></tr>
            ) : items.map((p, i) => (
              <tr key={p.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--rk-border)" }}>
                <td className="py-3 px-4 align-top">{p.show_name}</td>
                <td className="py-3 px-4 align-top">{p.episode_title}</td>
                <td className="py-3 px-4 align-top rk-caption">{(p.date || p.created_at || "").slice(0,10)}</td>
                <td className="py-3 px-4 align-top">{p.is_published ? "Published" : "Draft"}</td>
                <td className="py-3 px-4 align-top">
                  <div className="flex gap-3">
                    <button onClick={() => setEditing(p)} className="rk-btn-ghost">Edit</button>
                    <button onClick={() => remove(p.id)} className="rk-btn-ghost" style={{ color: "var(--rk-danger)" }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(hasToken());
  const [active, setActive] = useState("inquiries");
  const navigate = useNavigate();

  // Validate token on mount
  useEffect(() => {
    if (!hasToken()) {
      setAuthed(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
    toast.success("Signed out.");
  };

  if (!authed) return <LoginPanel onLoggedIn={() => setAuthed(true)} />;

  return (
    <section className="rk-section" style={{ paddingTop: 140 }}>
      <div className="rk-container-wide">
        <div className="flex items-center justify-between gap-6 mb-10">
          <div>
            <div className="rk-eyebrow">Admin</div>
            <h1 className="rk-heading-page mt-3" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>Dashboard.</h1>
          </div>
          <button onClick={logout} data-testid={ADMIN.logoutBtn} className="rk-btn-outline" style={{ height: 40 }}>
            Sign out
          </button>
        </div>

        <div
          data-testid={ADMIN.tabs}
          className="flex gap-1 mb-10"
          style={{ borderBottom: "1px solid var(--rk-border)" }}
        >
          {TABS.map((t) => {
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={ADMIN[`tab${t.key.charAt(0).toUpperCase() + t.key.slice(1)}`]}
                style={{
                  padding: "12px 18px",
                  background: "transparent",
                  border: "none",
                  borderBottom: isActive ? "2px solid var(--rk-copper)" : "2px solid transparent",
                  marginBottom: -1,
                  color: "var(--rk-ink)",
                  fontFamily: "var(--font-body)",
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  cursor: "pointer",
                  transition: "border-color 150ms ease",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {active === "inquiries" && <InquiriesView />}
        {active === "articles" && <ArticlesView />}
        {active === "podcasts" && <PodcastsView />}
        {active === "images" && <ImagesView />}
      </div>
    </section>
  );
}
